import { useEffect } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePostHog } from "@/analytics/usePostHog";
import { SEOHead } from "@/components/SEOHead";
import { breadcrumbSchema } from "@/lib/structuredData";
import { Clock, ChevronLeft } from "lucide-react";
import type { LocalizedArticle } from "@/data/localizedArticles";
import { localizePath } from "@/lib/localeRoutes";
import { localizeAppLinksInHtml } from "@/lib/appUrl";
import { useAppUrl } from "@/hooks/useAppUrl";
import {
  buildArticleSchema,
  getRelatedArticles,
} from "@/lib/articleSeo";
import { buildSeoUrls } from "@/lib/seo";
import { linksToDoctorVisits } from "@/lib/doctorVisitsArticleLinks";
import { ServiceArticleCallout } from "@/components/doctor-visits/ServiceArticleCallout";
import { AntalyaLocationCallout } from "@/components/locations/AntalyaLocationCallout";
import { linksToAntalyaLocations } from "@/lib/antalyaLocationArticleLinks";
import { getAudienceCtaHref } from "@/lib/ctaLinks";
import { ArticleImage } from "@/components/ArticleImage";
import { getArticleImage } from "@/lib/articleImages";
import { formatArticleDate } from "@/lib/articleDate";

const ARTICLE_OG_IMAGE = "https://dociohealth.com/og-article.png";

interface ArticleDetailProps {
  articles: LocalizedArticle[];
}

const ArticleDetail = ({ articles }: ArticleDetailProps) => {
  const { slug } = useParams<{ slug: string }>();
  const { t: translate, i18n } = useTranslation("articles");
  const locale = i18n.language;
  const posthog = usePostHog();
  const appUrl = useAppUrl();
  const { pathname } = useLocation();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      posthog?.capture('article_viewed', { slug: article.slug, locale });
    }
  }, [article, posthog, locale]);

  if (!article) return <Navigate to={localizePath("/articles", locale)} replace />;

  const t = article.translation;
  const image = getArticleImage(article.slug, locale);
  const { canonicalUrl } = buildSeoUrls(pathname);
  const articleSchema = buildArticleSchema(article, t, canonicalUrl);
  // Keep every related doctor link throughout the fixed six-article pilot.
  const relatedArticles = getRelatedArticles(article, articles, article.audience === "doctor" ? 5 : 3);
  const isInsuranceArticle = article.audience === "insurance";
  const isDoctorArticle = article.audience === "doctor";
  const detailTranslationKey = isDoctorArticle ? "doctorDetail" : isInsuranceArticle ? "insuranceDetail" : "detail";
  const articlesPath = localizePath("/articles", locale);
  const audienceArticlesPath = isDoctorArticle
    ? `${articlesPath}?audience=doctor`
    : isInsuranceArticle
    ? `${articlesPath}?audience=insurance`
    : articlesPath;
  const ctaHref = getAudienceCtaHref(
    isDoctorArticle ? "doctors" : isInsuranceArticle ? "insurance" : "patients",
    appUrl,
  );

  const crumbs = breadcrumbSchema([
    {
      name: translate("breadcrumbs.articles"),
      path: localizePath("/articles", locale),
    },
    {
      name: t.title,
      path: localizePath(`/articles/${article.slug}`, locale),
    },
  ], localizePath("/", locale));

  return (
    <>
      <SEOHead
        title={t.metaTitle}
        description={t.metaDescription}
        ogImage={image?.url ?? article.ogImage ?? ARTICLE_OG_IMAGE}
        ogImageAlt={image?.alt ?? t.ogImageAlt ?? article.ogImageAlt ?? `${t.title} - ${locale === "el" ? "άρθρο του Docio Health" : "Docio Health article"}`}
        ogImageWidth={image?.width}
        ogImageHeight={image?.height}
        ogImageType={image ? "image/webp" : undefined}
        ogType="article"
        jsonLd={[articleSchema, crumbs]}
      />
      <div className={locale === "el" ? "min-h-screen bg-muted break-words" : "min-h-screen bg-muted"}>
        {/* Hero */}
        <section className="bg-gradient-to-br from-accent to-secondary/5 py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <Link
              to={audienceArticlesPath}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />{" "}
              {translate(`${detailTranslationKey}.backToArticles`)}
            </Link>
            {isInsuranceArticle && (
              <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">
                {translate("insuranceDetail.badge")}
              </p>
            )}
            {isDoctorArticle && (
              <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">
                {translate("doctorDetail.badge")}
              </p>
            )}
            <div className="flex items-center space-x-3 text-sm text-muted-foreground mb-4">
              <span>
                {formatArticleDate(article.date, locale)}
              </span>
              <span>·</span>
              <span className="flex items-center space-x-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{translate("readTime", { minutes: article.readTimeMinutes })}</span>
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              {t.title}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t.excerpt}
            </p>
            <ArticleImage slug={article.slug} locale={locale} variant="cover" />
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose prose-lg prose-headings:font-bold prose-headings:text-foreground prose-p:text-foreground/80 prose-p:leading-relaxed prose-li:text-foreground/80 prose-strong:text-foreground prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-ul:space-y-2 max-w-none"
              dangerouslySetInnerHTML={{ __html: localizeAppLinksInHtml(t.content, locale) }}
            />

            {linksToDoctorVisits(article.slug) && (
              <ServiceArticleCallout articleSlug={article.slug} />
            )}
            {linksToAntalyaLocations(article.slug) && (
              <AntalyaLocationCallout articleSlug={article.slug} />
            )}

            {relatedArticles.length > 0 ? (
              <aside className="mt-14 border-t border-border pt-10">
                <h2 className="text-2xl font-bold text-foreground mb-5">
                  {translate(`${detailTranslationKey}.relatedTitle`)}
                </h2>
                <div className="grid gap-4">
                  {relatedArticles.map((relatedArticle) => {
                    const related = relatedArticle.translation;
                    return (
                      <Link
                        key={relatedArticle.slug}
                        to={localizePath(`/articles/${relatedArticle.slug}`, locale)}
                        className="group rounded-xl border border-border bg-background p-5 transition-colors hover:border-primary/40"
                      >
                        <h3 className="font-semibold text-foreground group-hover:text-primary">
                          {related.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                          {related.excerpt}
                        </p>
                        <span className="mt-3 inline-block text-sm font-medium text-primary">
                          {translate(`${detailTranslationKey}.relatedLink`)}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </aside>
            ) : null}

            {/* CTA */}
            <div className="mt-16 p-8 bg-secondary text-secondary-foreground rounded-2xl text-center">
              <h2 className="text-2xl font-bold mb-3">
                {translate(`${detailTranslationKey}.cta.title`)}
              </h2>
              <p className="text-secondary-foreground/70 mb-6">
                {translate(`${detailTranslationKey}.cta.description`)}
              </p>
              <Button
                size="lg"
                className={isInsuranceArticle
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-auto min-h-11 max-w-full whitespace-normal py-3"
                  : "bg-primary hover:bg-primary/90 text-primary-foreground px-8"}
                asChild
              >
                <a
                  href={ctaHref}
                  target={isInsuranceArticle ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  onClick={() => posthog?.capture('article_cta_clicked', { slug: article.slug, locale, audience: article.audience })}
                >
                  {translate(`${detailTranslationKey}.cta.button`)}
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ArticleDetail;
